import { CARDS } from "@reagent/content";
import { useGameStore } from "../../app/gameStore";
import { playCue } from "../../audio/AudioEngine";
import { boundCardRecipeLabel, costLabel, creditsLeft, labourKw, offerVisible, shortfall, shortfallLabel } from "./liveBuild";
import { InstallRecipePicker } from "./RecipePicker";
import "./setup-screen.css";

/**
 * The Play-phase catalogue (spec 0067): the Setup shelf, kept open while the
 * stage's `liveBuild` rule allows building mid-turn. Each offer states its ₵
 * price AND the kW labour before the player picks it, and an offer the ledger
 * or the power budget cannot cover is greyed with the reason in words.
 *
 * Reuses the Setup panel's classes, but sits bottom-left rather than over the
 * whole column: in Play the tile inspector still has to be readable above it.
 */
export function LiveBuildTray() {
  const snapshot = useGameStore((s) => s.snapshot);
  const pendingInstall = useGameStore((s) => s.pendingInstall);
  const pendingMove = useGameStore((s) => s.pendingMove);
  const selectSetupOffer = useGameStore((s) => s.selectSetupOffer);

  if (snapshot.phase !== "play" || snapshot.liveBuild === null) return null;

  const kw = labourKw(snapshot, "install");
  const offers = snapshot.setupCatalogue.filter((offer) =>
    offerVisible(snapshot, CARDS[offer.cardId]?.facility !== undefined),
  );

  return (
    <div className="setup setup--play">
      <div className="setup__panel">
        <header className="setup__head">
          <span className="setup__eyebrow">live build</span>
          <div className="setup__credits">
            <span className="setup__credits-value">{creditsLeft(snapshot)}</span>
            <span className="setup__credits-label">of {snapshot.setupBudgetCredits} ₵ left</span>
          </div>
        </header>

        {pendingMove !== null && <p className="setup__hint is-armed">Moving — click the destination on the board.</p>}

        <section className="setup__section">
          <h2>On offer · {kw} kW labour each</h2>
          {offers.length === 0 ? (
            <p className="setup__empty">Nothing on offer.</p>
          ) : (
            <div className="setup__offers">
              {offers.map((offer) => {
                const card = CARDS[offer.cardId];
                const selected = pendingInstall?.cardId === offer.cardId;
                const short = shortfall(snapshot, offer.costCredits, kw);
                // Selected stays clickable even once unaffordable, or there
                // would be no way to put it back down.
                const isDisabled = short !== null && !selected;
                const boundRecipe = boundCardRecipeLabel(snapshot, offer.cardId);
                return (
                  <button
                    key={offer.cardId}
                    className={`setup__offer${selected ? " is-selected" : ""}`}
                    disabled={isDisabled}
                    onClick={() => {
                      playCue(selected ? "uiClick" : "cardPick");
                      selectSetupOffer(selected ? null : offer.cardId);
                    }}
                  >
                    <span className="setup__offer-name">
                      {card?.name ?? offer.cardId}
                      {boundRecipe !== null && <small> — makes {boundRecipe}</small>}
                      {short && !selected && <small> — {shortfallLabel(short)}</small>}
                    </span>
                    <span className="setup__offer-cost">{costLabel(offer.costCredits, kw)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <InstallRecipePicker snapshot={snapshot} />
      </div>
    </div>
  );
}
